/* ===================================================================
   useSetupProgress() -- THE single source of truth for "how far is this
   account through setup?", shared by the unified checklist home
   (routes/Onboarding + the embedded zero-data dashboard checklist), the
   Finish-setup card, and any nudge that keys off onboarding completeness.

   DERIVED COMPLETION, NEVER "clicked Next" (spec 2026-07-07 §4). Each item is
   done ONLY when the underlying account state proves it -- read from the same
   real API signals every surface uses:

     Item 1  Connect Google   done = google_connection_status === 'connected'
                              (canonical: valid token + explicitly-confirmed
                               listing + >= 1 imported review -- lib/api/google)
     Item 2  Import customers  done = customer_count >= 1        [useCustomers]
     Item 3  Brand voice       done = brand_voice has content    [useBusiness]
     Item 4  Reactivation      done = past customers enrolled     (soft; see
                               the marker note below -- no server read exists)
     Item 5  First ask         done = requests_sent >= 1          [useRequests]
     Item 6  Widget            optional; NO cheap "installed" signal exists, so
                               it is never fabricated done (mirrors how the old
                               FirstRun omitted it). Never blocking.

   Item 0  Diagnostic       done = a saved two-tap answer / industry category
                            (lib/onboardingState). CORE-counted (chute step 1)
                            but `optional` so it never blocks activation.

   The COUNTED progress is the four CORE steps only (diagnostic, gbp, import,
   requests): every surface reads "X of 4". Voice / reactivation / widget are
   `core: false` -- derived SIGNALS kept for metrics + the never-blank-voice
   check, but never counted and never on the required path.

   REACTIVATION MARKER (Item 4): the backend exposes no tenant-wide "enrolled"
   read. Rather than fabricate a done, we read a per-tenant marker written ONLY
   on a SERVER-CONFIRMED enroll (reactivation-enroll returned enrolled >= 1).
   This is honest ("a real server action happened") but client-cached; when a
   backend read (e.g. an enrolled-count on /customers or /me) lands, switch the
   `reactivationDone` line to it. It is a SOFT/optional item, never blocking.

   All hooks are token-gated: before a session token lands they are disabled
   and everything reads not-yet-done (the safe pre-data state). While the
   signals are still loading we report `loading: true` so callers never flash a
   setup nudge at a fully-configured account.
   =================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { useBusiness } from './useBusiness';
import { useCustomers } from './useCustomers';
import { useMe } from './useMe';
import { useReports } from './useReports';
import { useRequests } from './useRequests';
import { useSessionToken } from './useSessionToken';
import type { BrandVoice } from '../lib/api';
import { getReactivationEnrolled, loadOnboarding } from '../lib/onboardingState';

/* A brand voice counts as "set" once any meaningful field carries content.
   Empty {} (the default for a never-touched account) does not. */
function brandVoiceIsSet(bv: BrandVoice | undefined): boolean {
  if (!bv) return false;
  const fields = ['tone', 'formality', 'signature', 'sample_phrases'] as const;
  return fields.some((f) => {
    const v = bv[f];
    return typeof v === 'string' && v.trim().length > 0;
  });
}

export type SetupStepId =
  | 'diagnostic'
  | 'gbp'
  | 'import'
  | 'voice'
  | 'reactivation'
  | 'requests'
  | 'widget';

/* Derived visual state per item. `in_progress` is only ever emitted where a
   real intermediate signal exists (Google's sub-status); the rest are binary
   done/not-started from real data -- honest, no invented middle state. */
export type SetupStepState = 'done' | 'in_progress' | 'not_started';

export interface SetupStep {
  id: SetupStepId;
  /* Short label. */
  title: string;
  /* One-line "why this matters" (checklist §13.2). */
  desc: string;
  cta: string;
  /* Legacy route target (kept for the Finish-setup card / any nudge that
     navigates rather than launching the in-place task). */
  route: string;
  done: boolean;
  /* Optional items never block "you're set up" (diagnostic, voice, reactivation,
     widget). The skippable diagnostic is optional here (never gates activation)
     yet still COUNTED via `core` below. */
  optional: boolean;
  /* CORE = counted in the slim "X of 4" progress denominator (the spec's linear
     chute: diagnostic, connect+confirm Google, add customers, first ask). The
     non-core items (voice, reactivation, widget) are off the required path and
     are never counted. */
  core: boolean;
  state: SetupStepState;
  /* Visual weight for the checklist home: the activation path (Connect + First
     ask) pulls emphasis; the widget is de-emphasized as "later". */
  emphasis: 'primary' | 'normal' | 'muted';
}

export interface SetupProgress {
  steps: SetupStep[];
  /* CORE progress only: doneCount / total count the four CORE steps (the slim
     chute), so every surface reads "X of 4" -- never the optional items. */
  doneCount: number;
  total: number;
  /* Percentage 0-100 across the CORE steps, for progress bars. */
  pct: number;
  /* All NON-optional items done (Connect + customers + first ask). This is the
     "you're set up" condition -- the optional widget/voice/reactivation do NOT
     block it. */
  coreComplete: boolean;
  /* True until the account is core-complete. Drives the Finish-setup nudge so
     it stops nagging once the activation path is done. */
  incomplete: boolean;
  /* The next best action -- the first not-done REQUIRED step (activation path),
     else the first not-done optional step, else null. */
  nextStep: SetupStep | null;
  /* True while the underlying signals are still loading (pre-token or in
     flight). Callers should not render a "finish setup" nudge while true. */
  loading: boolean;
}

export function useSetupProgress(): SetupProgress {
  // Bridge Supabase login -> data-plane token (deduped globally) so the
  // signal hooks can fire wherever this is mounted.
  useSessionToken();

  const meQ = useMe();
  const reportsQ = useReports();
  const customersQ = useCustomers({ limit: 1 });
  const businessQ = useBusiness();
  const requestsQ = useRequests({ limit: 1 });

  const clientId = meQ.data?.client_id;

  // The reactivation marker lives in localStorage (no server read). Read it
  // fresh each render and subscribe to its change event so a just-completed
  // enrollment flips the item without a reload, even across components.
  const [reactivationTick, setReactivationTick] = useState(0);
  useEffect(() => {
    const bump = () => setReactivationTick((n) => n + 1);
    window.addEventListener('rhody:reactivation-enrolled', bump);
    return () => window.removeEventListener('rhody:reactivation-enrolled', bump);
  }, []);
  const reactivationDone = getReactivationEnrolled(clientId);

  // Diagnostic (CORE step 1): done once the owner has saved either two-tap
  // answer or the industry category. Derived from the same local onboarding
  // store the diagnostic task writes (mirrors the reactivation marker pattern) --
  // a real saved answer, never a fabricated flag. Re-read each render so
  // completing it flips every surface's "X of 4".
  const savedDiag = loadOnboarding(clientId);
  const diagnosticDone = Boolean(
    savedDiag?.scheduleTool || savedDiag?.pastCustomerEstimate || savedDiag?.category,
  );

  return useMemo(() => {
    const customerCount = customersQ.data?.count ?? 0;
    const requestCount = requestsQ.data?.count ?? 0;
    const voiceSet = brandVoiceIsSet(businessQ.data?.brand_voice);

    // CANONICAL Google connection state (spec §2). "Connected" ONLY at
    // 'connected'; every intermediate status renders as in-progress ("Finish
    // connecting Google"), never a misleading done. Prefer /business's block,
    // fall back to /me; never derive connected from a bare token/location.
    const googleStatus =
      businessQ.data?.google_connection?.google_connection_status ??
      meQ.data?.google_connection_status ??
      'not_started';
    const googleConnected = googleStatus === 'connected';
    const googleState: SetupStepState = googleConnected
      ? 'done'
      : googleStatus === 'not_started'
        ? 'not_started'
        : 'in_progress'; // token_only | listing_pending_confirmation | confirmed_no_data

    const binary = (done: boolean): SetupStepState =>
      done ? 'done' : 'not_started';

    const steps: SetupStep[] = [
      {
        id: 'diagnostic',
        title: 'Answer 2 quick questions',
        desc: 'Tell us how you run the business so Rhody Reviews sets up the rest the right way, including your brand voice. Two taps.',
        cta: 'Answer 2 questions',
        route: '/onboarding',
        done: diagnosticDone,
        // Skippable -> never blocks activation (optional), but COUNTED as core
        // step 1 of the slim chute.
        optional: true,
        core: true,
        state: binary(diagnosticDone),
        emphasis: 'normal',
      },
      {
        id: 'gbp',
        title: 'Connect your Google Business Profile',
        desc: 'Your Google Business Profile is the listing customers leave reviews on (not your login). Connect it so your review link points to the right place.',
        cta: 'Connect Google Business Profile',
        route: '/settings',
        done: googleConnected,
        optional: false,
        core: true,
        state: googleState,
        emphasis: 'primary',
      },
      {
        id: 'import',
        title: 'Add your customers',
        desc: 'The people Rhody Reviews will ask. Connect your tool, upload a CSV, or add a few by hand.',
        cta: 'Add customers',
        route: '/customers',
        done: customerCount >= 1,
        optional: false,
        core: true,
        state: binary(customerCount >= 1),
        emphasis: 'normal',
      },
      {
        // Brand voice is ASSUMED (pre-filled from the diagnostic industry
        // default), never a shown checklist step -- the checklist filters it out
        // by `core: false`. Kept here only as a derived SIGNAL (metrics + the
        // account-never-blank check); it is not counted and not on the path.
        id: 'voice',
        title: 'Set your brand voice',
        desc: 'So every AI-drafted reply sounds like you. You approve each one before it posts.',
        cta: 'Set brand voice',
        route: '/settings',
        done: voiceSet,
        optional: true,
        core: false,
        state: binary(voiceSet),
        emphasis: 'normal',
      },
      {
        // Reactivation is surfaced as a POST-ACTIVATION dashboard push
        // (ReactivationPush), not a main-checklist row -> core: false, so it is
        // never counted and never on the required path.
        id: 'reactivation',
        title: 'Reactivate past customers',
        desc: 'The fastest path to your first review: ask the customers you have already earned.',
        cta: 'Set up reactivation',
        route: '/customers',
        done: reactivationDone,
        optional: true,
        core: false,
        state: binary(reactivationDone),
        emphasis: 'normal',
      },
      {
        id: 'requests',
        title: 'Send your first ask',
        desc: 'The finish line. Send your first review requests and the 5-stars start coming in.',
        cta: 'Send requests',
        route: '/requests',
        done: requestCount >= 1,
        optional: false,
        core: true,
        state: binary(requestCount >= 1),
        emphasis: 'primary',
      },
      {
        // Widget = explore-at-leisure, off the main path (core: false).
        id: 'widget',
        title: 'Add the review widget',
        desc: 'Nice once you have reviews to show. Paste one snippet on your site. Do this anytime later.',
        cta: 'Get the widget',
        route: '/widget',
        // No fabrication-free "installed" signal exists -> never auto-done.
        done: false,
        optional: true,
        core: false,
        state: 'not_started',
        emphasis: 'muted',
      },
    ];

    // PROGRESS COUNT = the four CORE steps only (diagnostic, connect+confirm
    // Google, add customers, first ask) -- the spec's slim linear chute. Every
    // surface reads "X of 4" off these; the optional items are never counted.
    const coreSteps = steps.filter((s) => s.core);
    const total = coreSteps.length; // 4
    const doneCount = coreSteps.filter((s) => s.done).length;

    // ACTIVATION ("you're set up") = the non-optional path only (Google
    // connected + >= 1 customer + first ask sent). The skippable diagnostic is
    // core-COUNTED but never blocks activation, so it stays optional here.
    const required = steps.filter((s) => !s.optional);
    const coreComplete = required.every((s) => s.done);

    // Next best action = the first not-done CORE step, in chute order. Optional
    // items (voice/reactivation/widget) are never surfaced as "next" -- they are
    // off the required path (reactivation is a post-activation push instead).
    const nextStep = coreSteps.find((s) => !s.done) ?? null;

    const loading =
      meQ.isLoading ||
      reportsQ.isLoading ||
      customersQ.isLoading ||
      businessQ.isLoading ||
      requestsQ.isLoading;

    return {
      steps,
      doneCount,
      total,
      pct: total > 0 ? Math.round((doneCount / total) * 100) : 0,
      coreComplete,
      incomplete: !coreComplete,
      nextStep,
      loading,
    };
  }, [
    meQ.data,
    meQ.isLoading,
    reportsQ.isLoading,
    customersQ.data,
    customersQ.isLoading,
    businessQ.data,
    businessQ.isLoading,
    requestsQ.data,
    requestsQ.isLoading,
    reactivationDone,
    reactivationTick,
    diagnosticDone,
  ]);
}
